// EstateFormModal.tsx

import React, {useState, useEffect, useMemo} from 'react';
import {
  addDoc,
  doc,
  updateDoc,
  Timestamp,
  GeoPoint,
  collection,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import {db, storage, firebaseAppId} from '../../../firebase';

// Leaflet Imports
import {MapContainer, TileLayer, Marker, useMapEvents} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Styled Components
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalActions,
  FormContainer,
  FileUploadContainer,
  MapContainerWrapper,
  Input,
  ProgressBar,
  ImagePreviewContainer,
  PreviewItem,
  DeleteButton,
} from '../ModeratorDashboard.styles';

// Fix for default Leaflet icon (KEEP this in the file where the map is used)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl:
    'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Component to handle map clicks (KEEP this local)
function LocationMarker({setLocation}: {setLocation: (lat: number, lng: number) => void}) {
  useMapEvents({
    click(e) {
      setLocation(e.latlng.lat, e.latlng.lng);
   },
 });
  return null;
}

// --- Interfaces and Constants (Moved from EstateManagement.tsx) ---
interface Estate {
  id: string;
  category: string;
  status: 'available' | 'sold' | 'pending';
  price: string; 
  currencyType?: 'UGX' | 'USD' | 'EUR'; 
  name?: string;
  description?: string;
  imageUrls: string[];
  videoUrls: string[]; 
  // MANDATORY FIELDS (Removed '?')
  latitude: number; 
  longitude: number;
  villageName?: string;
  logbookNames?: string;
  nin?: string;
  telephone: string; // Removed '?'
  telephone1?: string;
  telephone2?: string;
  whatsapp: string; // Removed '?'
  email?: string;
  logbookFileUrl?: string | null;
  make?: string;
  model?: string;
  bodyType?: string;
  fuel?: string;
  transmission?: string;
  steering?: string;
  chassisNumber?: string;
  yearManufacture?: number;
  yearRegistration?: number;
  landTitleOwnerNames?: string;
  landSize?: string;
  landTitleOwnerNin?: string;
  landTitleOwnerTelephone?: string;
  landTitleFileUrl?: string | null;
  buildingType?: string;
  noOfFloors?: number;
  noOfUnits?: number;
  roadStreetName?: string;
  plotNumber?: number;
  unitDescription?: string;
  propertyType?: string;
  selectedCountry?: string;
  selectedRegion?: string;
  selectedDistrict?: string;
  selectedSubcounty?: string;
  selectedParish?: string;
  selectedVillage?: string;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

interface FileWithPreview extends File {
  previewUrl: string;
}


const CATEGORIES = [
  'Land',
  'Residential Houses',
  'Equipment',
  'Buildings (Shopping Malls , Arcades, Apartment , Hotels , Restaurants , Schools , Hospitals , Rentals &Leasing)',
  'Cars',
  'General Property Sales',
  'Property Management'
];

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB for general files
const MAX_IMAGES = 10;
const MAX_VIDEOS = 5;

const defaultMapCenter: [number, number] = [0.3476, 32.5825]; // Kampala, Uganda

const getCollectionName = (category: string) => {
  return category.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_{2,}/g, '_');
};

// --- Media Preview Components ---

const VideoPreview = ({urls, onDelete}: {urls: string[], onDelete: (url: string) => void}) => (
    <ImagePreviewContainer>
      {urls.map((url, index) => (
        <PreviewItem key={index}>
          <video src={url} controls style={{maxWidth: '200px', maxHeight: '150px'}} />
          <DeleteButton type="button" onClick={() => onDelete(url)}>×</DeleteButton>
        </PreviewItem>
      ))}
    </ImagePreviewContainer>
);

const ImagePreview = ({urls, onDelete}: {urls: string[], onDelete: (url: string) => void}) => (
    <ImagePreviewContainer>
      {urls.map((url, index) => (
        <PreviewItem key={index}>
          <img src={url} alt={`Preview ${index}`} />
          <DeleteButton type="button" onClick={() => onDelete(url)}>×</DeleteButton>
        </PreviewItem>
      ))}
    </ImagePreviewContainer>
);

// --- Main Modal Component ---

interface EstateFormModalProps {
  isEditing: boolean;
  initialEstate: Estate | null;
  onClose: () => void;
}

const EstateFormModal: React.FC<EstateFormModalProps> = ({isEditing, initialEstate, onClose}) => {
  const [formState, setFormState] = useState<Partial<Estate>>(() => 
    initialEstate ? {
      ...initialEstate,
      videoUrls: initialEstate.videoUrls || [], 
      // Ensure mandatory fields have a value on edit
      telephone: initialEstate.telephone1 || initialEstate.telephone || '',
      whatsapp: initialEstate.telephone2 || initialEstate.whatsapp || '',
      latitude: initialEstate.latitude || 0,
      longitude: initialEstate.longitude || 0,
      // ... rest of fields
      email: initialEstate.email || '',
      category: initialEstate.category || 'Land'
    } : {
      // Provide default values for mandatory fields on add
      category: 'Land',
      status: 'available',
      price: '', 
      imageUrls: [],
      videoUrls: [],
      latitude: 0, // Default mandatory location
      longitude: 0, // Default mandatory location
      telephone: '', // Default mandatory contact
      whatsapp: '', // Default mandatory contact
    }
  );

  const [selectedImageFiles, setSelectedImageFiles] = useState<FileWithPreview[]>([]);
  const [selectedVideoFiles, setSelectedVideoFiles] = useState<FileWithPreview[]>([]);
  const [selectedLogbookFile, setSelectedLogbookFile] = useState<File | null>(null);
  const [selectedLandTitleFile, setSelectedLandTitleFile] = useState<File | null>(null);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [videosToDelete, setVideosToDelete] = useState<string[]>([]); 
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Utility Functions ---

  const uploadFileToStorage = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise<string>((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => reject(error),
        () => getDownloadURL(uploadTask.snapshot.ref).then(resolve)
      );
    });
  };

  const deleteFileFromStorage = async (url: string) => {
    if (!url) return;
    try {
      const fileRef = ref(storage, url);
      await deleteObject(fileRef);
    } catch (error: any) {
      if (error.code !== 'storage/object-not-found') {
        console.error('Error deleting file:', error);
      }
    }
  };


  // --- Input Handlers ---

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {id, value, type} = e.target;

    setFormState(prev => ({
      ...prev,
      [id]: type === 'number' ? Number(value) : value,
    }));
  };

  const handleMapLocationSelect = (lat: number, lng: number) => {
    setFormState(prev => ({...prev, latitude: lat, longitude: lng}));
  };

const attachPreviewUrls = (files: File[]): FileWithPreview[] =>
  files.map(file => {
    const previewUrl = URL.createObjectURL(file);
    return Object.assign(file, { previewUrl }) as FileWithPreview;
  });
//   
const handleFileChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  fileType: 'image' | 'video' | 'logbook' | 'landTitle'
) => {
  if (!e.target.files || e.target.files.length === 0) return;
  const filesArray = Array.from(e.target.files);
  let validationError = null;

  if (fileType === 'image') {
    const oversized = filesArray.some(file => file.size > MAX_IMAGE_SIZE);
    if (oversized) {
      validationError = `Images must be less than ${MAX_IMAGE_SIZE / (1024 * 1024)}MB each.`;
    } else if (
      filesArray.length +
        selectedImageFiles.length +
        (formState.imageUrls?.length || 0) -
        imagesToDelete.length >
      MAX_IMAGES
    ) {
      validationError = `You can upload a maximum of ${MAX_IMAGES} images.`;
    }

    if (!validationError) {
      const filesWithUrls = attachPreviewUrls(filesArray);
      setSelectedImageFiles(prev => [...prev, ...filesWithUrls]);
    }
  } else if (fileType === 'video') {
    const oversized = filesArray.some(file => file.size > MAX_VIDEO_SIZE);
    const nonVideo = filesArray.some(file => !file.type.startsWith('video/'));
    if (nonVideo) validationError = 'Please select only video files.';
    else if (oversized)
      validationError = `Videos must be less than ${MAX_VIDEO_SIZE / (1024 * 1024)}MB each.`;

    const totalVideos =
      (formState.videoUrls?.length || 0) +
      selectedVideoFiles.length +
      filesArray.length -
      videosToDelete.length;
    if (totalVideos > MAX_VIDEOS)
      validationError = `You can upload a maximum of ${MAX_VIDEOS} videos.`;

    if (!validationError) {
      const filesWithUrls = attachPreviewUrls(filesArray);
      setSelectedVideoFiles(prev => [...prev, ...filesWithUrls]);
    }
  } else if (fileType === 'logbook' || fileType === 'landTitle') {
    const file = filesArray[0];
    if (file.size > MAX_FILE_SIZE)
      validationError = `File must be less than ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
    if (fileType === 'logbook') setSelectedLogbookFile(validationError ? null : file);
    else setSelectedLandTitleFile(validationError ? null : file);
  }

  setError(validationError);
  e.target.value = ''; // reset input
};

const handleRemoveImage = (url: string) => {
  if (formState.imageUrls?.includes(url) && !url.startsWith('blob:')) {
    // Uploaded file (remove from DB list and mark for deletion)
    setImagesToDelete(prev => [...prev, url]);
    setFormState(prev => ({
      ...prev,
      imageUrls: prev.imageUrls?.filter(img => img !== url),
    }));
  } else {
    // Newly selected (not uploaded yet)
    setSelectedImageFiles(prev => {
      const updated = prev.filter(file => file.previewUrl !== url);
      const removed = prev.find(file => file.previewUrl === url);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return updated;
    });
  }
};

const handleRemoveVideo = (url: string) => {
  if (formState.videoUrls?.includes(url) && !url.startsWith('blob:')) {
    setVideosToDelete(prev => [...prev, url]);
    setFormState(prev => ({
      ...prev,
      videoUrls: prev.videoUrls?.filter(vid => vid !== url),
    }));
  } else {
    setSelectedVideoFiles(prev => {
      const updated = prev.filter(file => file.previewUrl !== url);
      const removed = prev.find(file => file.previewUrl === url);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return updated;
    });
  }
};
  // --- Media Removal Handlers (FIXED/Corrected Logic) ---

 


  // --- Submit Handler ---

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setUploadProgress(0);

    const isBuildingRelated = ['Residential Houses', 'Buildings (Shopping Malls , Arcades, Apartment , Hotels , Restaurants , Schools , Hospitals , Rentals &Leasing)','Land'].includes(formState.category || '');

    // 0. Pre-submission validation for mandatory fields
    if (!formState.latitude || !formState.longitude || !formState.telephone || !formState.whatsapp) {
        setError('Location (Latitude/Longitude) and Contact (Telephone/WhatsApp) fields are mandatory.');
        setLoading(false);
        return;
    }


    try {
        const collectionName = getCollectionName(formState.category || 'general_property');
        const storagePath = `estates/${firebaseAppId}/${collectionName}`;

        // 1. Upload new image files
        const uploadedImageUrls = await Promise.all(
            selectedImageFiles.map(file => uploadFileToStorage(file, `${storagePath}/${Date.now()}_${file.name}`))
        );

        // 2. Upload new video files
        const uploadedVideoUrls = await Promise.all(
            selectedVideoFiles.map(file => uploadFileToStorage(file, `${storagePath}/${Date.now()}_${file.name}`))
        );

        // 3. Upload other optional files
        const newLogbookFileUrl = selectedLogbookFile
            ? await uploadFileToStorage(selectedLogbookFile, `${storagePath}/${Date.now()}_${selectedLogbookFile.name}`)
            : null;

        const newLandTitleFileUrl = selectedLandTitleFile
            ? await uploadFileToStorage(selectedLandTitleFile, `${storagePath}/${Date.now()}_${selectedLandTitleFile.name}`)
            : null;

        // 4. Delete old files
        await Promise.all([
            ...imagesToDelete.map(url => deleteFileFromStorage(url)),
            ...videosToDelete.map(url => deleteFileFromStorage(url)), 
            (selectedLogbookFile || newLogbookFileUrl) && initialEstate?.logbookFileUrl ? deleteFileFromStorage(initialEstate.logbookFileUrl) : Promise.resolve(),
            (selectedLandTitleFile || newLandTitleFileUrl) && initialEstate?.landTitleFileUrl ? deleteFileFromStorage(initialEstate.landTitleFileUrl) : Promise.resolve(),
        ]);
        
        // 5. Construct the final data object for Firestore
        const finalImageUrls = [...(formState.imageUrls || []), ...uploadedImageUrls];
        const finalVideoUrls = [...(formState.videoUrls || []), ...uploadedVideoUrls];
        
        const estateData: Partial<Estate> = {
            ...formState,
            imageUrls: finalImageUrls,
            videoUrls: finalVideoUrls, 
            logbookFileUrl: newLogbookFileUrl || formState.logbookFileUrl || null,
            landTitleFileUrl: newLandTitleFileUrl || formState.landTitleFileUrl || null,
            updatedAt: Timestamp.now(),
            // Ensure mandatory fields are present for TypeScript compilation
            latitude: formState.latitude!, 
            longitude: formState.longitude!,
            telephone: formState.telephone!,
            whatsapp: formState.whatsapp!,
       };
       
       // Firestore doesn't need this block, as all fields are included in estateData above.
       /*
       if (estateData.latitude !== undefined && estateData.longitude !== undefined) {
   
       }
       */


        // 6. Add or Update document in the correct collection
        if (isEditing && initialEstate) {
            const estateDocRef = doc(db, `artifacts/${firebaseAppId}/${getCollectionName(initialEstate.category)}`, initialEstate.id);
            await updateDoc(estateDocRef, estateData);
       } else {
            const estatesRef = collection(db, `artifacts/${firebaseAppId}/${collectionName}`);
            await addDoc(estatesRef, {
                ...estateData,
                createdAt: Timestamp.now(),
           });
       }
        
        onClose();
   } catch (err) {
        console.error('Error saving estate:', err);
        setError(`Failed to ${isEditing ? 'update' : 'add'} estate. ` + (err as Error).message);
   } finally {
        setLoading(false);
        setUploadProgress(0);
   }
  };

  // --- Dynamic Form Fields Renderer ---
  const renderFormFields = () => {
    const {category} = formState;

    const commonFields = (
      <>
        <label htmlFor="name">Item/Estate Name:</label>
        <Input type="text" id="name" value={formState.name || ''} onChange={handleInputChange} />

        <label htmlFor="price">Price:</label>
          <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
            <Input
              type="text"
              id="price"
              value={formState.price || ''}
              onChange={handleInputChange}
              placeholder="e.g., $1000, 100 UGX, or Negotiable"
              style={{flex: 1}}
            />
          </div>

        <label htmlFor="description">Description:</label>
        <textarea id="description" value={formState.description || ''} onChange={handleInputChange} rows={6} />

        <label htmlFor="status">Status:</label>
        <select id="status" value={formState.status || 'available'} onChange={handleInputChange} >
          <option value="available">Available</option>
          <option value="sold">Sold</option>
          <option value="pending">Pending</option>
        </select>

        {/* CONTACT FIELDS - ALREADY RENDERED FOR ALL CATEGORIES */}
        <label htmlFor="telephone">Telephone:</label>
        <Input type="text" id="telephone" value={formState.telephone || ''} onChange={handleInputChange} />

        <label htmlFor="whatsapp">WhatsApp:</label>
        <Input type="text" id="whatsapp" value={formState.whatsapp || ''} onChange={handleInputChange} />

        <label htmlFor="email">Email:</label>
        <Input type="email" id="email" value={formState.email || ''} onChange={handleInputChange} />
      </>
    );

    const locationFields = (
      <>
        <label htmlFor="villageName">Village/Locality Name:</label>
        <Input type="text" id="villageName" value={formState.villageName || ''} onChange={handleInputChange} />
        <label>Select Location on Map:</label>
        <MapContainerWrapper>
          <MapContainer
            center={formState.latitude && formState.longitude ? [formState.latitude, formState.longitude] : defaultMapCenter}
            zoom={13}
            scrollWheelZoom={true}
            style={{height: '300px', width: '100%'}}
          >
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <LocationMarker setLocation={handleMapLocationSelect} />
            {formState.latitude !== 0 && formState.longitude !== 0 && <Marker position={[formState.latitude || 0, formState.longitude || 0]} />}
          </MapContainer>
        </MapContainerWrapper>
        {formState.latitude !== 0 && formState.longitude !== 0 && (
          <Input type="text" value={`Selected: Lat: ${formState.latitude?.toFixed(4)}, Lng: ${formState.longitude?.toFixed(4)}`} disabled style={{marginTop: '0.5rem'}} />
        )}
      </>
    );

    switch (category) {
      case 'Cars':
        return (
          <>
            <div className="form-column">
              {commonFields}
              {locationFields} {/* ADDED LOCATION FIELDS FOR CARS */}
            </div>
            <div className="form-column">
              <label htmlFor="make">Make:</label>
              <Input type="text" id="make" value={formState.make || ''} onChange={handleInputChange}  />
              <label htmlFor="model">Model:</label>
              <Input type="text" id="model" value={formState.model || ''} onChange={handleInputChange}  />
              <label htmlFor="bodyType">Body type:</label>
              <Input type="text" id="bodyType" value={formState.bodyType || ''} onChange={handleInputChange} />
              <label htmlFor="fuel">Fuel:</label>
              <Input type="text" id="fuel" value={formState.fuel || ''} onChange={handleInputChange} />
              <label htmlFor="transmission">Transmission:</label>
              <Input type="text" id="transmission" value={formState.transmission || ''} onChange={handleInputChange} />
              <label htmlFor="steering">Steering:</label>
              <Input type="text" id="steering" value={formState.steering || ''} onChange={handleInputChange} />
              <label htmlFor="chassisNumber">Chassis number:</label>
              <Input type="text" id="chassisNumber" value={formState.chassisNumber || ''} onChange={handleInputChange} />
              <label htmlFor="yearManufacture">Year of manufacture:</label>
              <Input type="number" id="yearManufacture" value={formState.yearManufacture || ''} onChange={handleInputChange} />
              <label htmlFor="yearRegistration">Year of Registration:</label>
              <Input type="number" id="yearRegistration" value={formState.yearRegistration || ''} onChange={handleInputChange} />
            </div>
          </>
        );

      case 'Land':
        return (
          <>
            <div className="form-column">
              {commonFields}
              {locationFields} {/* LOCATION FIELDS ALREADY HERE */}
            </div>
            <div className="form-column">
            </div>
          </>
        );

      case 'Residential Houses':
      case 'Buildings (Shopping Malls , Arcades, Apartment , Hotels , Restaurants , Schools , Hospitals , Rentals &Leasing)':
     
        return (
          <>
            <div className="form-column">
              {commonFields}
              {locationFields} {/* LOCATION FIELDS ALREADY HERE */}
            </div>
            <div className="form-column">
              <label htmlFor="buildingType">Building Type:</label>
              <select id="buildingType" value={formState.buildingType || ''} onChange={handleInputChange}>
                <option value="">Select a type...</option>
                <option value="Residential House">Residential House</option>
                <option value="Apartment">Apartment</option>
                <option value="Shopping Mall">Shopping Mall</option>
                <option value="Arcade">Arcade</option>
                <option value="Rental House">Rental House</option>
                <option value="School">School</option>
                <option value="Hospital">Hospital</option>
                <option value="Hotel/Restaurant">Hotel/Restaurant</option>
                <option value="Other">Other</option>
              </select>
              <label htmlFor="noOfFloors">Number of floors:</label>
              <Input type="number" id="noOfFloors" value={formState.noOfFloors || ''} onChange={handleInputChange} min="0" />
              <label htmlFor="noOfUnits">Number of units/rooms:</label>
              <Input type="number" id="noOfUnits" value={formState.noOfUnits || ''} onChange={handleInputChange} min="0" />
            </div>
          </>
        );

      case 'General Property Sales':
      case 'Equipment':
        return (
          <>
            <div className="form-column">
              {commonFields}
              {locationFields} {/* ADDED LOCATION FIELDS */}
            </div>
            <div className="form-column">
            </div>
          </>
        );
      
      case 'Property Management':
        return (
          <>
            <div className="form-column">
              {commonFields}
              {locationFields} {/* ADDED LOCATION FIELDS */}
              <label htmlFor="propertyType">Property type:</label>
              <select id="propertyType" value={formState.propertyType || ''} onChange={handleInputChange}>
                <option value="">Select a type...</option>
                <option value="Rental">Rental</option>
                <option value="Arcade">Arcade</option>
                <option value="Shopping Mall">Shopping Mall</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-column">
              <label htmlFor="roadStreetName">Road/street name:</label>
              <Input type="text" id="roadStreetName" value={formState.roadStreetName || ''} onChange={handleInputChange} />
              <label htmlFor="plotNumber">Plot Number:</label>
              <Input type="number" id="plotNumber" value={formState.plotNumber || ''} onChange={handleInputChange} />
              <label htmlFor="unitDescription">Description of Units/Rooms:</label>
              <textarea id="unitDescription" value={formState.unitDescription || ''} onChange={handleInputChange} rows={4} />
            </div>
          </>
        );
      
      default:
        return (
          <div className="form-column">
            {commonFields}
            {locationFields} {/* ADDED LOCATION FIELDS FOR DEFAULT */}
          </div>
        );
    }
  };


  return (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <h3>{isEditing ? 'Edit Item' : 'Add New Item'}</h3>
        </ModalHeader>
        <FormContainer>
          <form onSubmit={handleSubmit}>
            <div className="full-width">
              <label htmlFor="category">Category:</label>
              <select id="category" value={formState.category || 'Land Listings'} onChange={handleInputChange}>
                {CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            
            {renderFormFields()}

            <div className="full-width">
              {/* Error Display */}
              {error && <p style={{color: 'red', margin: '10px 0'}}>{error}</p>}
              
              <FileUploadContainer>
                {/* Image Upload */}
                <div className="file-input-group">
                  <label htmlFor="imageUpload">
                    Upload Images (Max {MAX_IMAGES}, {MAX_IMAGE_SIZE / (1024 * 1024)}MB each)
                  </label>
                  <input type="file" id="imageUpload" accept="image/*" multiple onChange={(e) => handleFileChange(e, 'image')} />
                  
                  {/* Current Images */}
                  {formState.imageUrls && formState.imageUrls.length > 0 && (
                    <>
                      <p>Current Images:</p>
                      <ImagePreview urls={formState.imageUrls} onDelete={handleRemoveImage} />
                    </>
                  )}
                  
                 {/* New Images to Upload */}
                {selectedImageFiles.length > 0 && (
                <>
                    <p>New Images to Upload:</p>
                    <ImagePreview 
                        urls={selectedImageFiles.map(file => file.previewUrl)} // <-- Use the stored previewUrl
                        onDelete={handleRemoveImage} 
                    />
                </>
                )}
                </div>
                
                {/* Video Upload Section for Multiple Files */}
                <div className="file-input-group">
                  <label htmlFor="videoUpload">
                    Upload Videos (Max {MAX_VIDEOS}, {MAX_VIDEO_SIZE / (1024 * 1024)}MB each)
                  </label>
                  <input type="file" id="videoUpload" accept="video/*" multiple onChange={(e) => handleFileChange(e, 'video')} />
                  
                  {/* Current Videos */}
                  {formState.videoUrls && formState.videoUrls.length > 0 && (
                    <div style={{marginTop: '10px'}}>
                      <p>Current Videos:</p>
                      <VideoPreview urls={formState.videoUrls} onDelete={handleRemoveVideo} />
                    </div>
                  )}
                  
                  {/* New Videos to Upload - *** FIXED: Using handleRemoveVideo for local files */}
                    {selectedVideoFiles.length > 0 && (
                    <div style={{marginTop: '10px'}}>
                        <p>New Videos to Upload:</p>
                        <VideoPreview 
                            urls={selectedVideoFiles.map(file => file.previewUrl)} // <-- Use the stored previewUrl
                            onDelete={handleRemoveVideo} 
                        />
                    </div>
                    )}
                </div>
                
              </FileUploadContainer>

              {uploadProgress > 0 && uploadProgress < 100 && (
                <ProgressBar>
                  <div style={{width: `${uploadProgress}%`}}></div>
                </ProgressBar>
              )}
            </div>

            <ModalActions className="full-width">
              <button type="button" onClick={onClose} className="cancel" disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="primary" disabled={loading || uploadProgress > 0}>
                {loading ? 'Saving...' : (isEditing ? 'Update Item' : 'Add Item')}
              </button>
            </ModalActions>
          </form>
        </FormContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EstateFormModal;
